// The "Add pages from a PDF" button under a description field, the page sets added this visit,
// and the text block they put in the field. The text itself is what is saved with the lesson.
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';
import { BLUE_TEXT, BORDER, INTER, NAVY, SLATE } from '../../style/verbaScholaStyles';
import type { PdfImportPayload } from '../../api/VerbaScholaApi';
import { importLine } from './importChecks';
import { pagesSourceLine, pdfPagesName } from './pageRanges';
import type { PdfDestination, PdfPagesResult } from './VerbaScholaPdfPagesDialog';

export interface AddedPdfPages {
    id: number;
    destination: PdfDestination;
    summary: string;
    block: string;
    /** Targets were chosen from these pages (Lesson brief): removing the pages leaves those targets in the lists. */
    withTargets: boolean;
}

// How a page's text was read, as noted under its marker; a page read from the PDF's own text has no note.
const readNotes: Record<PdfPagesResult['pages'][number]['method'], string> = {
    text: '', text_and_ocr: 'Text in its pictures read by OCR.', corrected_text: "The PDF's text, with pinyin read from the page by OCR.", ocr: 'Read by OCR.',
};

/** The pages' source line: the file and the pages, range by range (and their printed numbers). */
export function pdfPagesSourceLine(result: PdfPagesResult): string {
    return pagesSourceLine(result.fileName, result.pages);
}

/** What is saved of an import before its pages are added to the field: its pages (with their text as read and AI check) and their warnings (see importChecks). */
export function pdfImportPayload(result: PdfPagesResult, destination: PdfDestination): PdfImportPayload {
    return {
        lesson_field: destination === 'brief' ? 'brief' : 'ai_context_prompt_text',
        source_line: pdfPagesSourceLine(result),
        pages_json: result.pages.map(page => ({ page_number: page.pageNumber, label: page.label, method: page.method, read_text: page.readText, ai_check: page.aiCheck })),
        warnings_json: result.pages.flatMap(page => page.warnings.map(warning => ({
            page_number: page.pageNumber, kind: warning.kind, reading: warning.reading, why: warning.why, suggestion: warning.suggestion,
            evidence: warning.evidence, insert: warning.insert, region: warning.region, line_index: warning.lineIndex, context_indices: warning.contextIndices,
        }))),
    };
}

/**
 * The text added to the field: its first line, the source line and the saved import's id ("(import 12)"), which
 * finds the pages again for their warnings (see importChecks); then each page under a marker, with a note of how it was read when not from its own PDF
 * text, so later steps can tell OCR text from other text. The warnings are kept with the import, not in the text.
 * Quote checks leave these lines out (evidence_text on the server).
 */
export function pdfPagesBlock(result: PdfPagesResult, importId: number): string {
    const lines = [importLine(pdfPagesSourceLine(result), importId)];
    for (const page of result.pages) {
        const note = readNotes[page.method];
        // The prefix tells the note from a page's own first line in brackets (see evidence_text on the server).
        lines.push('', `===== PDF page ${page.pageNumber}${page.label ? ` (printed ${page.label})` : ''} =====`, ...(note ? [`[PDF import note: ${note}]`] : []), page.text);
    }
    return lines.join('\n');
}

export function pdfPagesSummary(result: PdfPagesResult): string {
    const ocr = result.pages.filter(page => page.method !== 'text').length;
    return `${result.fileName} · ${pdfPagesName(result.pages.map(page => page.pageNumber))}${ocr ? ` · ${ocr} read by OCR` : ''}`;
}

/** Appends a block to a field's text with a blank line between. */
export function withPdfPages(fieldText: string, block: string): string {
    return [fieldText.trimEnd(), block].filter(Boolean).join('\n\n');
}

/** Removes a block the field still holds unchanged; null when it was edited or removed. */
export function withoutPdfPages(fieldText: string, block: string): string | null {
    const at = fieldText.indexOf(block);
    if (at < 0) return null;
    return `${fieldText.slice(0, at).trimEnd()}\n\n${fieldText.slice(at + block.length).trimStart()}`.trim();
}

export default function PdfPagesSources({ sources, fieldText, note, onAdd, onRemove }: {
    sources: AddedPdfPages[];
    fieldText: string;
    /** Said under the pages, as after removing some (null for nothing). */
    note?: string | null;
    onAdd: () => void;
    onRemove: (source: AddedPdfPages) => void;
}) {
    return (
        <Stack spacing={1} sx={{ mt: 1.25 }}>
            {sources.map(source => {
                const present = fieldText.includes(source.block);
                return (
                    <Stack key={source.id} direction="row" alignItems="center" spacing={1.25} sx={{ px: 1.5, py: 1, border: `1px solid ${BORDER}`, borderRadius: '12px', backgroundColor: '#fff' }}>
                        <PictureAsPdfOutlinedIcon sx={{ fontSize: 20, color: BLUE_TEXT, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: NAVY, overflowWrap: 'anywhere' }}>{source.summary}</Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE }}>{present ? 'Added to the text above.' : 'Edited in the text above; change it there.'}</Typography>
                        </Box>
                        {present && <Button size="small" onClick={() => onRemove(source)} sx={{ flexShrink: 0, fontFamily: INTER, fontWeight: 700, textTransform: 'none', color: SLATE }}>Remove</Button>}
                    </Stack>
                );
            })}
            {note && <Typography role="status" sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>{note}</Typography>}
            <Box>
                <Button variant="outlined" startIcon={sources.length ? <AddRoundedIcon /> : <PictureAsPdfOutlinedIcon />} onClick={onAdd}
                    sx={{ minHeight: 44, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>
                    {sources.length ? 'Add more pages from a PDF' : 'Add pages from a PDF'}
                </Button>
            </Box>
        </Stack>
    );
}
